import {
  LinkOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks';
import { WebhookIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { FormattedConnectionModel } from '@mm/state/model/ConnectionModel';
import { CaptureActionEnum } from '@mm/state/model/SuppressionSettingsModel';
import { selectJSONWebhooksConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectWebhookSuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ConfigProvider, Table } from 'antd';
import { ColumnType } from 'antd/es/table/interface';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { HelpCenter } from '../../../../../common/components/HelpCenter/HelpCenter';
import { autoIncrementedName } from '../../../../../common/helpers/name-autoincrement';
import { BaseIntegration } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegration';
import { BaseIntegrationEmptyState } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegrationEmptyState';
import { BaseIntegrationHeader } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegrationHeader';
import { ReactComponent as AddWebhookIcon } from '../../../beta/modules/Integrations/icons/add-webhook.svg';
import { ContactSuppressionHeader } from '../../../beta/modules/Integrations/NativeIntegrations/components/ContactSuppressions/ContactSuppressionHeader';
import SuppressionWebhookLists from '../../../beta/modules/Integrations/NativeIntegrations/components/ContactSuppressions/SuppressionWebhookLists';
import './ConnectionsPanel.pcss';

type Props = {
  cellData: ColumnType<FormattedConnectionModel>[];
};

export const UNTITLED_CONNECTION_NAME = 'Untitled connection';

export const ConnectionsPanel = (props: Props) => {
  const { cellData } = props;
  const connections = useSelector(selectJSONWebhooksConnections);
  const connectedPageId = useSelector(activeFacebookPageIdSelector);
  const connectionsCRUDActions = useConnectionCRUDActions();
  const suppressionListsCRUDActions = useSuppressionListsCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const leadsSent =
    (crmsLeadsSent?.jsonWebhookContactsSent || 0) +
    (crmsLeadsSent?.customJsonContactsSent || 0);

  const webhookSuppressions = useSelector(selectWebhookSuppressionList);
  const webhookSuppressionLists = webhookSuppressions.filter(
    w => w.captureAction === CaptureActionEnum.do_not_send || !w.captureAction,
  );

  const webhookUnsubscribeLists = webhookSuppressions.filter(
    w => w.captureAction === CaptureActionEnum.do_not_capture,
  );

  const onNewConnection = async () => {
    const name = autoIncrementedName(
      UNTITLED_CONNECTION_NAME,
      connections.map(c => c.name),
    );

    const connection = await connectionsCRUDActions.create(connectedPageId, {
      name,
      connectionType: 'Webhook',
      description: '',
    });

    if (connection) {
      showSlideoutMenu('editJSONWebhookIntegrationEditor', {
        webhookId: connection.id,
      });
    }
  };

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_JSON_WEBHOOKS,
    featureFlag: 'jsonWebhook',
  };

  const handleSuppressionWebhookClick = async (
    captureAction: CaptureActionEnum,
  ) => {
    const isSuppression = captureAction === CaptureActionEnum.do_not_send;
    const webhookSuppressions = isSuppression
      ? webhookSuppressionLists
      : webhookUnsubscribeLists;
    const UNTITLED_SUPPRESSION_NAME = `New Webhook ${
      isSuppression ? 'Suppression' : 'Unsubscribe'
    } List`;

    const incrementedName =
      webhookSuppressions.length === 0
        ? UNTITLED_SUPPRESSION_NAME
        : autoIncrementedName(
            UNTITLED_SUPPRESSION_NAME,
            webhookSuppressions.map(c => c.name),
          );

    const newSuppressionList = await suppressionListsCRUDActions.create(
      connectedPageId,
      {
        enabled: false,
        sourceKind: 'suppression_webhook',
        name: incrementedName,
        captureAction,
      },
    );

    showSlideoutMenu('editWebhoookSuppressionListEditor', {
      suppressionListId: newSuppressionList.id,
    });
  };

  const header = (
    <div className="integrations__header-actions">
      <HelpCenter
        helpLink="https://customers.ai/help/l/en/article/stqu70gqm1-custom-webhooks"
        icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
        title=""
      />

      {!!connections.length && (
        <div className="integrations__header-status">
          <LinkOutlined className="integrations__header-status-icon" />
          <div className="integrations__header-status-count">
            {connections?.length || 0}
          </div>
          Connections
        </div>
      )}

      {!!leadsSent && (
        <div className="integrations__header-status">
          <TeamOutlined className="integrations__header-status-icon" />
          <div className="integrations__header-status-count">{leadsSent}</div>
          Leads sent
        </div>
      )}

      <PrimaryButton
        className="integrations__header-add-button-secondary"
        onClick={e => {
          e?.stopPropagation();
          onNewConnection();
        }}
        color={ButtonColor.secondaryBlue}
      >
        Add a Connection
      </PrimaryButton>
    </div>
  );

  const emptyState = (buttonName, onClick) => (
    <BaseIntegrationEmptyState
      button={buttonName}
      onClick={onClick}
      icon={<AddWebhookIcon />}
      {...sectionProps}
    />
  );

  return (
    <BaseIntegration
      openOnMount
      titleComponent={
        <BaseIntegrationHeader
          title="JSON Webhooks "
          subtitle={`Automatically send data from ${companyName} to JSON Webhook`}
          icon={<WebhookIcon color="white" />}
          css={{ icon: 'integrations__webhook-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="default"
      emptyStateConfig={{
        button: 'Add a JSON Webhooks Connection',
        onClick: onNewConnection,
        icon: <AddWebhookIcon />,
        ...sectionProps,
      }}
      showEmptyState={!connections.length}
    >
      <div className="connections-panel__content-wrapper">
        <div className="connections-panel__label">Sources</div>
        <div className="connections-panel__content">
          <ContactSuppressionHeader
            isSuppression
            count={webhookSuppressionLists?.length}
            onClick={() =>
              handleSuppressionWebhookClick(CaptureActionEnum.do_not_send)
            }
          />

          {webhookSuppressionLists.length ? (
            <SuppressionWebhookLists
              captureAction={CaptureActionEnum.do_not_send}
            />
          ) : (
            emptyState('Add a JSON Webhook Suppression List', () =>
              handleSuppressionWebhookClick(CaptureActionEnum.do_not_send),
            )
          )}
        </div>
        <div className="connections-panel__content">
          <ContactSuppressionHeader
            isSuppression={false}
            count={webhookUnsubscribeLists?.length}
            onClick={() =>
              handleSuppressionWebhookClick(CaptureActionEnum.do_not_capture)
            }
          />

          {webhookUnsubscribeLists.length ? (
            <SuppressionWebhookLists
              captureAction={CaptureActionEnum.do_not_capture}
            />
          ) : (
            emptyState('Add a JSON Webhook Unsubscribe List', () =>
              handleSuppressionWebhookClick(CaptureActionEnum.do_not_capture),
            )
          )}
        </div>
      </div>
      <div
        className="connections-panel__connections-wrapper"
        style={{ marginTop: 16 }}
      >
        <div className="connections-panel__connections-label">Destinations</div>
        <div className="connections-panel__connections-content">
          <div className="connections-panel__connections-header">
            <div className="connections-panel__connections-header-title">
              CRM connections
              <div className="connections-panel__connections-header-subtitle">
                Send leads directly to your CRM
              </div>
            </div>
            {header}
          </div>
        </div>
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: 'white',
                borderColor: '#D9D9D9',
                cellPaddingInlineSM: 16,
              },
            },
          }}
        >
          <div className="connections-panel-table">
            <Table
              dataSource={connections}
              columns={cellData}
              pagination={false}
              size="middle"
              bordered
            />
          </div>
        </ConfigProvider>
      </div>
    </BaseIntegration>
  );
};
